// @ts-check
import { isObject } from 'lodash';

/**
 * Text, or an object value that opens as a JSON preview.
 * @typedef {{ text: string, isStrong?: boolean } | { diff: object }} ChangePart
 */

/**
 * Only the first item of a change list can be a collapsible group.
 * @typedef {{ type: 'text', parts: ChangePart[] }
 *   | { type: 'property', key: string, before: ChangePart[], after: ChangePart[] }
 *   | { type: 'group', heading: string, items: ChangeItem[], isCollapsible: boolean }} ChangeItem
 */

/**
 * Builds the displayed list of changes from a jsondiffpatch delta.
 * The delta is not modified.
 * @param {unknown} delta
 * @returns {ChangeItem[]}
 */
export function buildChangeList(delta) {
  const items = buildItems(delta, undefined);

  if (items[0]?.type === 'group') {
    items[0].isCollapsible = true;
  }

  return items;
}

/**
 * @param {any} delta
 * @param {string | number | undefined} key
 * @returns {ChangeItem[]}
 */
function buildItems(delta, key) {
  if (Array.isArray(delta)) {
    const item = buildLeaf(delta, key);
    return item ? [item] : [];
  }

  if (delta === null || typeof delta !== 'object') {
    return [];
  }

  const items = childEntries(delta).flatMap(([childKey, child]) => buildItems(child, childKey));

  if (key === undefined) {
    return items;
  }

  const heading = typeof key === 'number' ? 'Index ' + key + ' updated' : key;
  return [{ type: 'group', heading, items, isCollapsible: false }];
}

/**
 * Array deltas store the children under their index, and the removed ones under `_index`.
 * @param {Record<string, unknown>} delta
 * @returns {[string | number, unknown][]}
 */
function childEntries(delta) {
  const keys = Object.keys(delta);

  if (delta._t === 'a') {
    return keys
      .filter(key => key !== '_t')
      .sort((a, b) => arrayKeyOrder(a) - arrayKeyOrder(b))
      .map(key => [parseInt(trimUnderscore(key), 10), delta[key]]);
  }

  return keys.sort().map(key => [key, delta[key]]);
}

/** @param {string} key */
function arrayKeyOrder(key) {
  return key.startsWith('_') ? parseInt(key.slice(1), 10) : parseInt(key, 10) + 0.1;
}

/** @param {string} key */
function trimUnderscore(key) {
  return key.startsWith('_') ? key.slice(1) : key;
}

/**
 * @param {any[]} delta
 * @param {string | number | undefined} key
 * @returns {ChangeItem | undefined}
 */
function buildLeaf(delta, key) {
  if (delta.length === 1) {
    return added(delta, key);
  }

  if (delta.length === 2) {
    return modified(delta, key);
  }

  if (delta.length === 3 && delta[2] === 0) {
    return deleted(delta, key);
  }

  if (delta.length === 3 && delta[2] === 2) {
    return textDiff(delta, key);
  }

  if (delta.length === 3 && delta[2] === 3) {
    return moved(delta, key);
  }

  return undefined;
}

/**
 * @param {any[]} delta
 * @param {string | number | undefined} key
 * @returns {ChangeItem}
 */
function added(delta, key) {
  if (key === undefined) {
    return line('New value');
  }

  if (typeof key === 'number') {
    return line('Inserted ', value(delta[0]), ' at index ', strong(key));
  }

  return line('Added property ', strong(key), ' (', value(delta[0]), ')');
}

/**
 * @param {any[]} delta
 * @param {string | number | undefined} key
 * @returns {ChangeItem}
 */
function modified(delta, key) {
  if (delta[0] == null && delta[1] != null) {
    return line('New value for ', strong(key), ': ', value(delta[1]));
  }

  if (key === undefined) {
    return line('Updated value');
  }

  if (typeof key === 'number') {
    return line('Updated index ', strong(key), ': ', value(delta[0]), ' => ', value(delta[1]));
  }

  return { type: 'property', key, before: [value(delta[0])], after: [value(delta[1])] };
}

/**
 * @param {any[]} delta
 * @param {string | number | undefined} key
 * @returns {ChangeItem}
 */
function deleted(delta, key) {
  if (key === undefined) {
    return line('Deleted value');
  }

  if (typeof key === 'number') {
    return line('Removed index ', strong(key), ' (', value(delta[0]), ')');
  }

  return line('Deleted property ', strong(key), ' (', value(delta[0]), ')');
}

/**
 * @param {any[]} delta
 * @param {string | number | undefined} key
 * @returns {ChangeItem}
 */
function moved(delta, key) {
  return line('Move from index ', strong(key), ' to index ', value(delta[1]));
}

/**
 * @param {any[]} delta
 * @param {string | number | undefined} key
 * @returns {ChangeItem}
 */
function textDiff(delta, key) {
  const location = key === undefined
    ? []
    : [' at ' + (typeof key === 'number' ? 'index ' : 'property '), strong(key)];

  return line('Text update', ...location, ': ' + delta[0] + ' => ' + delta[1]);
}

/**
 * @param {...(string | ChangePart)} parts
 * @returns {ChangeItem}
 */
function line(...parts) {
  return { type: 'text', parts: parts.map(part => (typeof part === 'string' ? { text: part } : part)) };
}

/** @param {unknown} text */
function strong(text) {
  return { text: String(text), isStrong: true };
}

/**
 * @param {unknown} data
 * @returns {ChangePart}
 */
function value(data) {
  return isObject(data) ? { diff: data } : { text: String(data) };
}
